/**
 * APLICAR UNA TARJETA DE CAMBIOS DEL CUADERNO (CQ4b · A20).
 *
 * Lo llama «Aplicar» en `AssistantCuadernoChangeCard`: escribe el NAVEGADOR,
 * con la sesión de la persona (RLS), nunca la edge del asistente. Antes de
 * tocar nada vuelve a validar la tarjeta entera con el contrato cerrado
 * (`cambios.ts#validarCambio`) —viaja por la red y se guarda en el historial—
 * y comprueba el RECUENTO: si los datos cambiaron desde que se propuso, no se
 * aplica y se dice.
 *
 *   (CU9, 28-09-2026) Las tarjetas del DOCUMENTO (`esCambioDelDocumento`:
 *              editar, añadir o quitar líneas) se aplican SOLAS al llegar, por esta
 *              misma función; cada operación lleva `cambio` = el `change_id` de la
 *              tarjeta (y `deshacer: true` las de «Deshacer»), que la RPC guarda en
 *              su evento de auditoría. `estadoDelCambio` lo lee para saber, al
 *              reabrir la conversación, si la tarjeta está hecha o deshecha.
 *   borrador → el borrador de (empresa, titular, año) con la RPC
 *              `ensure_cuaderno_draft` (lo crea vacío si no lo hay: la tabla no
 *              deja INSERT a nadie, solo se escribe por RPC) y después
 *              `apply_cuaderno_change(p_draft_id, p_ops, p_expected_count)` con
 *              `p_expected_count = recuentoDeOps(ops)` (CQ4a: filtra el alcance,
 *              rechaza sacar un fitosanitario, deja evento de auditoría). Sus
 *              errores se leen con `leerErrorCambio` (borrador.ts), no con
 *              expresiones sobre el texto.
 *   parte    → edge `update-spray-log-details` (F123) una vez por tarea o parte
 *              manual. Antes, los partes de la tarjeta se recuentan POR ID: una
 *              tarea que ganó o perdió partes, o un parte borrado, paran todo.
 *   legal    → `farm_legal_profiles` (CQ1), los campos de la ronda (CU7) en UNA
 *              sentencia sobre la fila del titular (todo o nada); solo el admin
 *              (lo impone la RLS; 0 filas actualizadas = sin permiso, no
 *              «aplicado»). La fila de una finca de otro titular tiene que
 *              existir (M5): solo la del titular de la empresa se crea aquí. Un
 *              asesor sin nº ROPO de asesor no se guarda (D18).
 *   maquinaria → `company_equipment` con `equipmentApi` (CU7): alta (si no hay
 *              ya una viva con esa descripción) o el nº de una que ya existe.
 *   aplicador → `company_applicators` con `applicatorApi` (CU7): una empresa de
 *              servicios (alta o sus datos) o el carné de una persona (su fila
 *              viva por `user_id`, o una nueva), sin borrar lo demás de la ficha.
 *   sigpac   → `greenhouse_sigpac_recintos` con `escribirRecintoManual` (CU7):
 *              lo mismo que «Escribir a mano» de Ajustes, confirmado.
 *
 * E11: los errores no llevan valores (un NIF nunca va a un mensaje de error).
 */
import {
  cuerpoDeParte,
  esUuid,
  nombreLimpio,
  validarCambio,
  type CambioCuaderno,
  type ObjetivoParte,
  type OpCarne,
  type OpCuaderno,
  type OpDatosFinca,
  type OpEmpresaServicios,
  type OpMaquina,
  type OpSigpac,
} from "./cambios";
import { leerErrorCambio, recuentoDeOps, type CuadernoOp, type ResultadoCambio } from "./borrador";
import { abrirBorrador, type ErrorRpc } from "./congelar";
import { escribirRecintoManual } from "./sigpacNaves";
import { parsePerLocation } from "./datosLegales";
import { fetchCompanyEquipment, saveEquipment, setEquipmentNumber } from "@/lib/machinery/equipmentApi";
import { equipmentRegime } from "@/lib/machinery/equipmentRules";
import {
  applicatorDraftProblem,
  draftFromApplicator,
  fetchCompanyApplicators,
  fetchMemberApplicator,
  saveApplicator,
  type ApplicatorDraft,
} from "@/lib/applicators/applicatorApi";

export type MotivoFallo = "invalida" | "recuento" | "permiso" | "rechazada" | "error";

export type ResultadoAplicar =
  | { ok: true; aplicadas: number; version?: number | null; aviso?: "recuento_distinto"; esperado?: number }
  | {
      ok: false;
      motivo: MotivoFallo;
      detalle?: string;
      esperado?: number;
      encontrado?: number;
      aplicadas?: number;
      /** Motivos de rechazo de la RPC del borrador, sin repetir (`fitosanitario_registrado`…). Sin ids ni valores (E11). */
      rechazos?: string[];
    };

/* El cliente de Supabase del navegador (o uno de mentira en las pruebas): las
   tablas y la RPC del cuaderno aún no están en los tipos generados. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Sb = any;

export interface DepsAplicar {
  supabase: Sb;
  companyId: string;
  /** Quien pulsa «Aplicar»: autor del alta y de la confirmación del SIGPAC (CU7). */
  userId?: string | null;
}

const PERMISO = /permission|denied|42501|forbidden|not allowed|row-level security|PGRST301|scope|alcance/i;
const RECUENTO = /expected|recuento|count|mismatch|stale|P0003/i;

function clasificar(error: { message?: string; code?: string } | null | undefined): "permiso" | "recuento" | "error" {
  const txt = `${error?.code ?? ""} ${error?.message ?? ""}`;
  if (PERMISO.test(txt)) return "permiso";
  if (RECUENTO.test(txt)) return "recuento";
  return "error";
}

const trozos = <T,>(xs: T[], n: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += n) out.push(xs.slice(i, i + n));
  return out;
};

/* Los errores de las RPC del borrador (ver la migración 20260926140000). */
/* CUADERNO_APAGADO (L8): la empresa tiene el cuaderno apagado; para quien mira es un «no puedes». */
const PERMISO_RPC = new Set(["CUADERNO_SIN_SESION", "CUADERNO_SIN_PERMISO", "CUADERNO_FUERA_DE_ALCANCE", "CUADERNO_FINCA_AJENA", "CUADERNO_APAGADO"]);
/* CUADERNO_TITULAR_SIN_FICHA (M5): la finca ya no es de otro titular (se quitó su fila de
   datos legales): la propuesta era para un cuaderno que ya no existe. */
const DATOS_CAMBIADOS_RPC = new Set(["CUADERNO_RECUENTO", "CUADERNO_BORRADOR_NO_EXISTE", "CUADERNO_TITULAR_SIN_FICHA"]);

/**
 * Un error de `ensure_cuaderno_draft` / `apply_cuaderno_change`, leído con
 * `leerErrorCambio`: el código de la RPC decide, y de los rechazos solo se
 * guardan los MOTIVOS (sin línea ni valor, E11) para que la tarjeta diga por qué.
 */
export function falloDelBorrador(error: ErrorRpc | null | undefined): Omit<Extract<ResultadoAplicar, { ok: false }>, "ok"> {
  const e = leerErrorCambio(error);
  if (DATOS_CAMBIADOS_RPC.has(e.codigo)) {
    return { motivo: "recuento", ...(e.recuento ? { esperado: e.recuento.esperado ?? undefined, encontrado: e.recuento.aplicables } : {}) };
  }
  if (PERMISO_RPC.has(e.codigo)) return { motivo: "permiso" };
  if (e.codigo === "CUADERNO_OPS_RECHAZADAS") {
    const rechazos = [...new Set(e.rechazos.map((r) => String(r.motivo)))];
    // Todo lo rechazado es de fincas o naves que no son suyas: es un permiso.
    if (rechazos.length && rechazos.every((m) => m === "fuera_de_alcance")) return { motivo: "permiso", rechazos };
    return { motivo: "rechazada", rechazos };
  }
  if (e.codigo === "CUADERNO_OPS_INVALIDAS") return { motivo: "invalida" };
  return { motivo: clasificar(error) };
}

async function aplicarBorrador(deps: DepsAplicar, c: CambioCuaderno): Promise<ResultadoAplicar> {
  // CU9: cada operación con la tarjeta que la aplica (queda en la auditoría; «Deshacer» ya la trae).
  const ops = (c.ops as CuadernoOp[]).map((o) => (o.cambio === undefined && esUuid(c.change_id) ? { ...o, cambio: c.change_id } : o)) as CuadernoOp[];
  // El borrador solo se consigue (y se crea) por RPC, con la empresa de la SESIÓN, no la de la tarjeta.
  const b = await abrirBorrador(deps.supabase, { companyId: deps.companyId, titularLocationId: c.titular_location_id, year: c.year });
  if (!b.id) return { ok: false, ...falloDelBorrador(b.error) };
  // La propuesta se hizo sobre OTRO borrador (se borró y se volvió a abrir): los datos cambiaron.
  if (c.draft_id && c.draft_id.toLowerCase() !== b.id.toLowerCase()) return { ok: false, motivo: "recuento" };
  const esperado = recuentoDeOps(ops);
  const { data, error } = await deps.supabase.rpc("apply_cuaderno_change", {
    p_draft_id: b.id,
    p_ops: ops,
    p_expected_count: esperado,
  });
  if (error) return { ok: false, esperado, ...falloDelBorrador(error) };
  const r = (Array.isArray(data) ? data[0] : data) as Partial<ResultadoCambio> | null;
  return { ok: true, aplicadas: Number(r?.applied ?? esperado), version: r?.version ?? null };
}

/** Tareas (o partes sueltos) por llamada a `update-spray-log-details` en lote. */
export const TAREAS_POR_LLAMADA = 500;

type OpParte = Extract<OpCuaderno, { objetivo: ObjetivoParte }>;

/**
 * ¿Siguen siendo los mismos partes? Cada id de la tarjeta tiene que existir (y
 * ser de la empresa) y cada tarea tiene que tener EXACTAMENTE sus partes: la
 * edge escribe en todos los de la tarea, así que una tarea que ganó un sector
 * cambiaría más de lo que la persona vio.
 */
export async function recontarPartes(deps: DepsAplicar, ops: OpParte[]): Promise<{ ok: boolean; esperado: number; encontrado: number }> {
  const ids = [...new Set(ops.flatMap((o) => o.objetivo.spray_log_ids))];
  const tareas = [...new Set(ops.map((o) => o.objetivo.task_id).filter(Boolean) as string[])];
  const esperadosDeTareas = new Set(ops.filter((o) => o.objetivo.task_id).flatMap((o) => o.objetivo.spray_log_ids));
  let encontrados = 0;
  for (const t of trozos(ids, 150)) {
    const { data, error } = await deps.supabase.from("spray_logs").select("id").eq("company_id", deps.companyId).in("id", t);
    if (error) return { ok: false, esperado: ids.length, encontrado: -1 };
    encontrados += (data ?? []).length;
  }
  let deTareas = 0;
  for (const t of trozos(tareas, 150)) {
    const { data, error } = await deps.supabase.from("spray_logs").select("id").eq("company_id", deps.companyId).in("task_id", t);
    if (error) return { ok: false, esperado: ids.length, encontrado: -1 };
    deTareas += (data ?? []).length;
  }
  const ok = encontrados === ids.length && deTareas === esperadosDeTareas.size;
  return { ok, esperado: ids.length, encontrado: encontrados + (deTareas - esperadosDeTareas.size) };
}

async function aplicarPartes(deps: DepsAplicar, c: CambioCuaderno): Promise<ResultadoAplicar> {
  const ops = c.ops as OpParte[];
  const r = await recontarPartes(deps, ops);
  if (!r.ok) return { ok: false, motivo: r.encontrado < 0 ? "error" : "recuento", esperado: r.esperado, encontrado: r.encontrado };
  /* EN LOTE (27-09-2026): antes era UNA llamada a la edge por tarea, en fila; «para
     todos los partes, el atomizador de Las Norias» eran 322 llamadas y minutos sin
     acabar. Ahora las operaciones con el MISMO cambio van juntas: una llamada por cada
     500 tareas (la edge lee, calcula en paralelo y escribe todo en una transacción). */
  const porCambio = new Map<string, { changes: Record<string, unknown>; tareas: string[]; partes: string[] }>();
  for (const op of ops) {
    const cuerpo = cuerpoDeParte(op) as { task_id?: string; spray_log_id?: string; changes: Record<string, unknown> };
    const k = JSON.stringify(cuerpo.changes);
    const g = porCambio.get(k) ?? { changes: cuerpo.changes, tareas: [], partes: [] };
    if (cuerpo.task_id) g.tareas.push(cuerpo.task_id);
    else if (cuerpo.spray_log_id) g.partes.push(cuerpo.spray_log_id);
    porCambio.set(k, g);
  }
  let aplicadas = 0;
  for (const g of porCambio.values()) {
    const lotes = [
      ...trozos(g.tareas, TAREAS_POR_LLAMADA).map((t) => ({ task_ids: t, changes: g.changes })),
      ...trozos(g.partes, TAREAS_POR_LLAMADA).map((p) => ({ spray_log_ids: p, changes: g.changes })),
    ];
    for (const body of lotes) {
      const { data, error } = await deps.supabase.functions.invoke("update-spray-log-details", { body });
      if (error) {
        const status = (error as { context?: { status?: number } }).context?.status;
        return { ok: false, motivo: status === 403 ? "permiso" : clasificar(error), aplicadas, esperado: c.expected_count };
      }
      aplicadas += Number((data as { updated?: number } | null)?.updated ?? 0);
    }
  }
  return aplicadas === c.expected_count ? { ok: true, aplicadas } : { ok: true, aplicadas, aviso: "recuento_distinto", esperado: c.expected_count };
}

async function aplicarLegal(deps: DepsAplicar, c: CambioCuaderno): Promise<ResultadoAplicar> {
  const valores: Record<string, unknown> = {};
  for (const op of c.ops) if (op.op === "set_legal_field") valores[op.campo] = op.valor;
  const fincas = c.ops.filter((o): o is OpDatosFinca => o.op === "set_datos_finca");
  let q = deps.supabase.from("farm_legal_profiles").select("id, asesor_ropo, per_location").eq("company_id", deps.companyId);
  q = c.titular_location_id ? q.eq("location_id", c.titular_location_id) : q.is("location_id", null);
  const { data: filas, error: errLeer } = await q.limit(1);
  if (errLeer) return { ok: false, motivo: clasificar(errLeer) };
  const fila = Array.isArray(filas) && filas[0]?.id ? (filas[0] as { id: string; asesor_ropo?: string | null; per_location?: unknown }) : null;
  const id = fila ? String(fila.id) : null;
  /* M5 (D14): una finca solo es de OTRO titular si ya tiene su fila; si no la tiene, no
     se crea aquí en silencio (eso partiría el cuaderno de la empresa en dos). Se para
     como «los datos han cambiado». La del titular de la empresa (location_id NULL) sí
     se crea al primer dato. */
  if (!id && c.titular_location_id) return { ok: false, motivo: "recuento" };
  /* D18 (como el formulario de Ajustes): un asesor sin nº ROPO de asesor no se guarda.
     El ROPO puede venir en la misma tarjeta o estar ya guardado. */
  if (valores.asesor_nombre && !valores.asesor_ropo && !String(fila?.asesor_ropo ?? "").trim()) {
    return { ok: false, motivo: "invalida", detalle: "asesor_sin_ropo" };
  }
  /* El 2.2 por finca (CU7): se MEZCLA con lo que ya tenían las otras fincas (jsonb de la
     misma fila), en la misma sentencia. */
  if (fincas.length) {
    const actual = parsePerLocation(fila?.per_location);
    for (const f of fincas) actual[f.location_id] = f.datos;
    valores.per_location = actual;
  }
  /* UNA sentencia con todos los campos de la ronda (CU7): todo o nada. El UPDATE devuelve
     las filas que tocó: 0 = la RLS no le deja (solo el admin escribe, CQ1), y eso NO es
     «aplicado» (un encargado que lee la fila la actualizaba «sin error» y sin cambiar nada). */
  const res = id
    ? await deps.supabase.from("farm_legal_profiles").update(valores).eq("id", id).eq("company_id", deps.companyId).select("id")
    : await deps.supabase.from("farm_legal_profiles").insert({ ...valores, company_id: deps.companyId, location_id: null });
  if (res.error) return { ok: false, motivo: clasificar(res.error) };
  if (id && !(Array.isArray(res.data) && res.data.length === 1)) return { ok: false, motivo: "permiso" };
  return { ok: true, aplicadas: c.ops.length };
}

/* ── Los ajustes del cuaderno (CU7) ─────────────────────────────────────────── */

/** Un error de la base al escribir un ajuste → motivo de la tarjeta (sin valores, E11). */
function falloAjuste(error: unknown): Extract<ResultadoAplicar, { ok: false }> {
  const e = (error ?? {}) as { code?: string; message?: string };
  // El trigger de la tabla: la persona ya no es de la empresa, o la finca no es suya.
  if (/APPLICATOR_USER_NOT_IN_COMPANY|EQUIPMENT_LOCATION_NOT_IN_COMPANY|la nave no existe/.test(e.message ?? "")) return { ok: false, motivo: "recuento" };
  // Otra fila viva con la misma clave (un carné por persona): alguien la dio de alta mientras tanto.
  if (e.code === "23505") return { ok: false, motivo: "recuento" };
  return { ok: false, motivo: clasificar(e) };
}

const mismaCosa = (a: string | null | undefined, b: string | null | undefined) =>
  nombreLimpio(a).toLocaleLowerCase("es") === nombreLimpio(b).toLocaleLowerCase("es");

async function aplicarMaquina(deps: DepsAplicar, op: OpMaquina): Promise<ResultadoAplicar> {
  if (op.equipment_id) {
    const r = await setEquipmentNumber(deps.companyId, op.equipment_id, op.roma as string, deps.supabase);
    if (r === "ok") return { ok: true, aplicadas: 1 };
    if (r === "permiso") return { ok: false, motivo: "permiso" };
    // Ya no está, o es una mochila (exenta): lo que se propuso ya no cuadra con la ficha.
    return { ok: false, motivo: r === "exenta" ? "invalida" : "recuento" };
  }
  // Alta: si mientras tanto alguien dio de alta una con el mismo nombre, se para (no se duplica).
  const vivas = await fetchCompanyEquipment(deps.companyId, {}, deps.supabase);
  if (vivas.some((e) => mismaCosa(e.description, op.descripcion))) return { ok: false, motivo: "recuento" };
  const censo = equipmentRegime(op.tipo) === "censo";
  await saveEquipment(
    deps.companyId,
    deps.userId ?? null,
    {
      equipment_type: op.tipo!,
      description: op.descripcion as string,
      brand: "",
      model: "",
      roma_number: censo ? "" : op.roma ?? "",
      census_reference: censo ? op.roma ?? "" : "",
      acquired_on: "",
      last_inspection_on: "",
      location_id: op.location_id,
      is_default: false,
    },
    null,
    deps.supabase,
  );
  return { ok: true, aplicadas: 1 };
}

async function aplicarEmpresaServicios(deps: DepsAplicar, op: OpEmpresaServicios): Promise<ResultadoAplicar> {
  const vivas = await fetchCompanyApplicators(deps.companyId, deps.supabase);
  const servicios = vivas.filter((a) => a.kind === "service_company");
  let actual = null as (typeof vivas)[number] | null;
  if (op.applicator_id) {
    actual = servicios.find((a) => a.id === op.applicator_id) ?? null;
    // La que se propuso cambiar ya no está (o ya no es una empresa de servicios).
    if (!actual) return { ok: false, motivo: "recuento" };
  } else if (servicios.some((a) => mismaCosa(a.display_name, op.nombre) || (a.nif && a.nif === op.nif))) {
    // Alta de una que ya existe (mismo nombre o NIF): alguien la dio de alta mientras tanto.
    return { ok: false, motivo: "recuento" };
  }
  // Sobre lo que ya tiene la ficha (asesor, tipo…): solo cambia lo que se dijo.
  const draft: ApplicatorDraft = {
    ...draftFromApplicator(actual, { kind: "service_company", user_id: null }),
    kind: "service_company",
    user_id: null,
    display_name: op.nombre,
    nif: op.nif,
    ropo_number: op.ropo,
    // Sin caducidad dicha: la expedición + 10 años (saveApplicator, como el trigger de la tabla).
    license_expires_on: op.caduca ?? "",
    license_issued_on: op.expedido ?? actual?.license_issued_on ?? "",
  };
  if (draft.license_expires_on && draft.license_issued_on > draft.license_expires_on) draft.license_issued_on = op.expedido ?? "";
  if (applicatorDraftProblem(draft)) return { ok: false, motivo: "invalida" };
  await saveApplicator(deps.companyId, deps.userId ?? null, draft, actual?.id ?? null, deps.supabase);
  return { ok: true, aplicadas: 1 };
}

async function aplicarCarne(deps: DepsAplicar, op: OpCarne): Promise<ResultadoAplicar> {
  const actual = await fetchMemberApplicator(deps.companyId, op.user_id, deps.supabase);
  const draft: ApplicatorDraft = {
    ...draftFromApplicator(actual, { kind: "member", user_id: op.user_id }),
    kind: "member",
    user_id: op.user_id,
    // El nombre de la ficha si ya tenía carné; si no, el de la persona.
    display_name: actual?.display_name?.trim() || op.nombre,
    ropo_number: op.ropo,
    license_type: op.carne,
    license_expires_on: op.caduca,
    license_issued_on: op.expedido ?? actual?.license_issued_on ?? "",
    ...(op.nif ? { nif: op.nif } : {}),
  };
  // La expedición guardada puede ser posterior a la caducidad nueva: entonces no se guarda la vieja.
  if (draft.license_issued_on && draft.license_issued_on > draft.license_expires_on) draft.license_issued_on = op.expedido ?? "";
  if (applicatorDraftProblem(draft)) return { ok: false, motivo: "invalida" };
  await saveApplicator(deps.companyId, deps.userId ?? null, draft, actual?.id ?? null, deps.supabase);
  return { ok: true, aplicadas: 1 };
}

async function aplicarSigpac(deps: DepsAplicar, op: OpSigpac): Promise<ResultadoAplicar> {
  // La nave sigue existiendo y es de la empresa (la RLS de greenhouses es de empresa).
  const { data, error } = await deps.supabase.from("greenhouses").select("id").eq("company_id", deps.companyId).eq("id", op.greenhouse_id).limit(1);
  if (error) return { ok: false, motivo: clasificar(error) };
  if (!Array.isArray(data) || data.length !== 1) return { ok: false, motivo: "recuento" };
  const r = await escribirRecintoManual(deps.supabase, { companyId: deps.companyId, naveId: op.greenhouse_id, codes: op.codigos, userId: deps.userId ?? null });
  if (r.error) return falloAjuste(r.error);
  return { ok: true, aplicadas: 1 };
}

async function aplicarAjuste(deps: DepsAplicar, c: CambioCuaderno): Promise<ResultadoAplicar> {
  const op = c.ops[0];
  try {
    switch (op.op) {
      case "upsert_equipment":
        return await aplicarMaquina(deps, op);
      case "upsert_service_company":
        return await aplicarEmpresaServicios(deps, op);
      case "set_member_license":
        return await aplicarCarne(deps, op);
      case "set_sigpac":
        return await aplicarSigpac(deps, op);
      default:
        return { ok: false, motivo: "invalida" };
    }
  } catch (e) {
    return falloAjuste(e);
  }
}

/** Qué pasó con una tarjeta del documento (CU9), según la auditoría del borrador. */
export type EstadoCambio = "hecho" | "deshecho" | "ninguno" | "desconocido";

/**
 * ¿Está aplicada (o deshecha) la tarjeta `changeId`? Lee `cuaderno_draft_events` (la
 * RLS: admin y encargado de la empresa) por la marca `cambio` que dejó la RPC: el
 * ÚLTIMO evento manda (uno con `deshacer` = deshecha). Sin eventos, «ninguno»; si no
 * se puede leer, «desconocido». Así una tarjeta del historial no se vuelve a
 * aplicar sola ni enseña «Hecho» de algo que se deshizo.
 */
export async function estadoDelCambio(supabase: Sb, companyId: string, changeId: string): Promise<EstadoCambio> {
  if (!esUuid(changeId)) return "desconocido";
  try {
    const { data, error } = await supabase
      .from("cuaderno_draft_events")
      .select("payload, at")
      .eq("company_id", companyId)
      .eq("payload->>cambio", changeId.toLowerCase())
      .order("at", { ascending: false })
      .limit(1);
    if (error) return "desconocido";
    const ultimo = Array.isArray(data) ? (data[0] as { payload?: { deshacer?: unknown } } | undefined) : undefined;
    if (!ultimo) return "ninguno";
    return ultimo.payload?.deshacer === true ? "deshecho" : "hecho";
  } catch {
    return "desconocido";
  }
}

/** Aplica una tarjeta `cuaderno_change`. Nunca lanza. */
export async function aplicarCambioCuaderno(deps: DepsAplicar, raw: unknown): Promise<ResultadoAplicar> {
  const v = validarCambio(raw);
  if (!v.ok) return { ok: false, motivo: "invalida", detalle: "motivo" in v ? v.motivo : undefined };
  const c = v.cambio;
  try {
    if (c.tipo === "borrador") return await aplicarBorrador(deps, c);
    if (c.tipo === "parte") return await aplicarPartes(deps, c);
    if (c.tipo === "legal") return await aplicarLegal(deps, c);
    return await aplicarAjuste(deps, c);
  } catch {
    return { ok: false, motivo: "error" };
  }
}
